import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Descriptions, Dropdown, message, Modal, Table, Tag, Tooltip, Typography, Menu } from "antd";
import { DeleteOutlined, EditOutlined, EyeOutlined, MobileOutlined, MoreOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import {
  deleteMessageTemplate,
  getMessageTemplates,
  getMessageTemplateTypes,
  submitMessageTemplate,
} from "../../../api/Services";
import styles from "./CampaignTemplates.module.scss";
import { useNavigate, useLocation } from "react-router-dom";

type TemplateStatus =
  | "DRAFT"
  | "SUBMITTED"
  | "APPROVED"
  | "REJECTED"
  | "PAUSED"
  | "DISABLED"
  | "UNKNOWN";

const statusColor: Record<string, string> = {
  DRAFT: "default",
  SUBMITTED: "processing",
  APPROVED: "success",
  REJECTED: "error",
  PAUSED: "warning",
  DISABLED: "default",
  UNKNOWN: "default",
};

const CampaignTemplates: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/campaigns";
    }
    return "/promotions/campaigns";
  };

  const tr = useCallback(
    (key: string, fallback: string) => {
      const value = t(key);
      return value === key ? fallback : value;
    },
    [t]
  );

  const pickLabel = useCallback((item: any) => {
    // Default: always show Spanish descriptions for template enums
    return item?.description_es;
  }, []);

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [refreshRequested, setRefreshRequested] = useState(false);
  const [page, setPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;
  const [types, setTypes] = useState<{
    statuses: any[];
    languages: any[];
    categories: any[];
  }>({ statuses: [], languages: [], categories: [] });
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  const fetchTemplates = useCallback(async (nextPage: number = page) => {
    setLoading(true);
    try {
      const resp = await getMessageTemplates({
        refresh: true, // always refresh statuses from provider
        page: Math.max(0, (nextPage || 1) - 1),
        size: pageSize,
      });
      setTemplates(resp?.templates || []);
      setTotalElements(resp?.totalElements || 0);
    } catch (e) {
      console.error("Error fetching templates:", e);
      message.error(t("Common-ErrorLoadingData"));
      setTemplates([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, t]);

  useEffect(() => {
    fetchTemplates(1);
    getMessageTemplateTypes().then(setTypes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStatusLabel = useCallback(
    (value: TemplateStatus) => {
      const found = (types.statuses || []).find((x: any) => x.value === value);
      return found ? pickLabel(found) : value;
    },
    [pickLabel, types.statuses]
  );

  useEffect(() => {
    if (refreshRequested) {
      fetchTemplates(page).finally(() => setRefreshRequested(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshRequested]);

  const columns = useMemo(
    () => [
      {
        title: tr("Common-Name", "Nombre"),
        dataIndex: "name",
        render: (name: string) => name || "-",
      },
      {
        title: tr("Common-Language", "Idioma"),
        dataIndex: "language",
        render: (lang: string) => {
          const found = (types.languages || []).find((x: any) => x.value === lang);
          return found ? pickLabel(found) : (lang || "-");
        },
      },
      {
        title: tr("Common-Category", "Categoría"),
        dataIndex: "category",
        render: (cat: string) => {
          const found = (types.categories || []).find((x: any) => x.value === cat);
          return found ? pickLabel(found) : (cat || "-");
        },
      },
      {
        title: tr("Common-Status", "Estado"),
        dataIndex: "status",
        render: (status: TemplateStatus) => (
          <Tag color={statusColor[status] || "default"}>
            {getStatusLabel(status || "UNKNOWN")}
          </Tag>
        ),
      },
      {
        title: tr("Common-Actions", "Acciones"),
        key: "actions",
        render: (_: any, record: any) => (
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="view"
                  icon={<EyeOutlined />}
                  onClick={() => {
                    setSelectedTemplate(record);
                    setPreviewModalVisible(true);
                  }}
                >
                  {tr("Common-View", "Ver")}
                </Menu.Item>
                {record?.status === "DRAFT" && (
                  <>
                    <Menu.Item
                      key="edit"
                      icon={<EditOutlined />}
                      onClick={() => navigate(`${getBasePath()}/templates/${record.id}/edit`)}
                    >
                      {tr("Common-Edit", "Editar")}
                    </Menu.Item>
                    <Menu.Item
                      key="submit"
                      icon={<EditOutlined />}
                      onClick={async () => {
                        const updatedTemplate = await submitMessageTemplate(record.id);
                        if (updatedTemplate) {
                          message.success(
                            tr("Common-SubmitSuccessMessage", "Enviada a revisión correctamente")
                          );
                          setTemplates((prev) =>
                            prev.map((t) =>
                              t.id === record.id ? { ...t, ...updatedTemplate } : t
                            )
                          );
                        } else {
                          message.error(tr("Common-SubmitErrorMessage", "Error al enviar a revisión"));
                        }
                      }}
                    >
                      {tr("Common-SubmitForReview", "Enviar a revisión")}
                    </Menu.Item>
                  </>
                )}
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  danger
                  onClick={() => {
                    Modal.confirm({
                      title: tr("Common-DeleteConfirm", "¿Eliminar?"),
                      okText: tr("Common-Yes", "Sí"),
                      cancelText: tr("Common-No", "No"),
                      okType: "danger",
                      onOk: async () => {
                        const ok = await deleteMessageTemplate(record.id);
                        if (ok) {
                          message.success(tr("Common-DeleteSuccessMessage", "Eliminado correctamente"));
                          await fetchTemplates(page);
                        } else {
                          message.error(tr("Common-DeleteErrorMessage", "Error al eliminar"));
                        }
                      },
                    });
                  }}
                >
                  {tr("Common-Delete", "Eliminar")}
                </Menu.Item>
              </Menu>
            }
          >
            <Button className={styles.actionButtonMore}>
              <MoreOutlined className={styles.actionIconMore} />
            </Button>
          </Dropdown>
        ),
      },
    ],
    [fetchTemplates, getStatusLabel, page, pickLabel, tr, types.categories, types.languages]
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.actionsLeft}>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => setRefreshRequested(true)}
            loading={refreshRequested}
          >
            {tr("Common-Refresh", "Actualizar")}
          </Button>
        </div>
        <div className={styles.actionsRight}>
          <Tooltip title={tr("Common-Create", "Crear")}>
            <Button
              className={styles.newButton}
              icon={<PlusOutlined />}
              onClick={() => {
                navigate(`${getBasePath()}/templates/create`);
              }}
            />
          </Tooltip>
        </div>
      </div>

      <Table
        size="small"
        className={styles.table}
        rowKey="id"
        columns={columns as any}
        dataSource={templates}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total: totalElements,
          showSizeChanger: false,
          onChange: (next) => {
            setPage(next);
            fetchTemplates(next);
          },
        }}
      />

      <Modal
        visible={previewModalVisible}
        title={tr("Common-TemplatePreview", "Vista previa de plantilla")}
        onCancel={() => {
          setPreviewModalVisible(false);
          setSelectedTemplate(null);
        }}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            {tr("Common-Close", "Cerrar")}
          </Button>,
        ]}
        width={700}
      >
        {selectedTemplate && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label={tr("Common-Name", "Nombre")} span={2}>
                {selectedTemplate.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={tr("Common-Language", "Idioma")}>
                {pickLabel(
                  (types.languages || []).find((x: any) => x.value === selectedTemplate.language)
                ) || selectedTemplate.language || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={tr("Common-Category", "Categoría")}>
                {pickLabel(
                  (types.categories || []).find((x: any) => x.value === selectedTemplate.category)
                ) || selectedTemplate.category || "-"}
              </Descriptions.Item>
              <Descriptions.Item label={tr("Common-Status", "Estado")} span={2}>
                <Tag color={statusColor[selectedTemplate.status] || "default"}>
                  {getStatusLabel(selectedTemplate.status || "UNKNOWN")}
                </Tag>
              </Descriptions.Item>
              {selectedTemplate.providerTemplateSid && (
                <Descriptions.Item label="Content SID" span={2}>
                  <Typography.Text code copyable>
                    {selectedTemplate.providerTemplateSid}
                  </Typography.Text>
                </Descriptions.Item>
              )}
              {selectedTemplate.rejectionReason && (
                <Descriptions.Item label={tr("Common-Reason", "Razón")} span={2}>
                  <Typography.Text type="danger">{selectedTemplate.rejectionReason}</Typography.Text>
                </Descriptions.Item>
              )}
            </Descriptions>

            <div className={styles.previewPhone}>
              <div className={styles.previewScreen}>
                <div className={styles.whatsappHeader}>
                  <MobileOutlined className={styles.whatsappHeaderIcon} />
                  <span>{tr("Common-YourPhoneNumber", "Tu número de teléfono")}</span>
                </div>
                <div className={styles.chatArea}>
                  <div className={styles.bubble}>
                    {selectedTemplate.body || tr("Common-PreviewEmpty", "La vista previa aparecerá aquí.")}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};

export default CampaignTemplates;

